import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useLanguage } from "../../contexts/LanguageContext";
import { useAuth } from "../../contexts/AuthContext";
import { Globe, Menu, X, User, LogOut } from "lucide-react";

const Header = ({ onPreorderClick, onLoginClick }) => {
  const { t, language, toggleLanguage } = useLanguage();
  const { user, logout } = useAuth();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 50);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToSection = (id) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: "smooth" });
    }
    setMobileMenuOpen(false);
  };

  const handleLoginClick = () => {
    setMobileMenuOpen(false);
    if (onLoginClick) {
      onLoginClick();
    } else {
      onPreorderClick();
    }
  };

  return (
    <header className={`calli-header transition-all duration-300 ${scrolled ? 'py-2 shadow-lg' : 'py-4'}`} data-testid="header">
      <div className="calli-container">
        <div className="flex items-center justify-between">
          {/* Logo */}
          <a 
            href="/" 
            className="text-2xl md:text-3xl font-black gradient-text"
            data-testid="logo"
          >
            calli
          </a>

          {/* Desktop Nav */}
          <nav className="hidden md:flex items-center gap-8">
            {['features', 'how-it-works', 'faq'].map((section) => (
              <button 
                key={section}
                onClick={() => scrollToSection(section)}
                className="text-gray-600 hover:text-orange-500 transition-all duration-300 font-medium text-sm uppercase tracking-wider"
                data-testid={`nav-${section}`}
              >
                {t(`nav_${section.replace(/-/g, '_')}`)}
              </button>
            ))}
            <a 
              href="/blog"
              className="text-gray-600 hover:text-orange-500 transition-all duration-300 font-medium text-sm uppercase tracking-wider"
              data-testid="nav-blog"
            >
              Blog
            </a>
          </nav>

          {/* Right side */}
          <div className="flex items-center gap-2">
            {/* Language Switch - Plus compact sur mobile */}
            <button 
              onClick={toggleLanguage}
              className="flex items-center gap-1 bg-orange-50 border border-orange-200 rounded-full px-2 py-1.5 md:px-3 md:py-2 text-xs md:text-sm font-bold text-orange-600 hover:bg-orange-100 transition-all"
              data-testid="language-switch"
            >
              <Globe className="w-3 h-3 md:w-4 md:h-4" />
              <span>{language.toUpperCase()}</span>
            </button>

            {/* Account Icon - Desktop only */}
            {user ? (
              <div className="hidden md:flex items-center gap-2">
                <button
                  onClick={() => navigate("/dashboard")}
                  className="flex items-center gap-2 bg-green-50 border border-green-200 rounded-full px-3 py-2 text-sm font-bold text-green-600 hover:bg-green-100 transition-all"
                  data-testid="nav-account"
                >
                  <User className="w-4 h-4" />
                  <span className="hidden lg:inline">{t("nav_dashboard")}</span>
                </button>
                <button
                  onClick={logout}
                  className="p-2 text-gray-400 hover:text-red-500 transition-colors"
                  data-testid="nav-logout"
                  title={t("nav_logout")}
                >
                  <LogOut className="w-4 h-4" />
                </button>
              </div>
            ) : (
              <button
                onClick={handleLoginClick}
                className="hidden md:flex items-center gap-2 bg-gray-100 border border-gray-200 rounded-full px-3 py-2 text-sm font-medium text-gray-600 hover:bg-orange-50 hover:border-orange-200 hover:text-orange-600 transition-all"
                data-testid="nav-login"
              >
                <User className="w-4 h-4" />
                <span className="hidden lg:inline">{language === 'fr' ? 'Connexion' : 'Login'}</span>
              </button>
            )}

            {/* Preorder CTA */}
            <button
              onClick={onPreorderClick}
              className="hidden md:flex calli-btn-primary h-10 px-5 text-sm"
              data-testid="nav-preorder"
            >
              {t("nav_preorder")}
            </button>

            {/* Mobile Menu Toggle */}
            <button
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="md:hidden p-2 text-orange-500"
              data-testid="mobile-menu-toggle"
            >
              {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>
        </div>

        {/* Mobile Menu */}
        {mobileMenuOpen && (
          <div className="md:hidden mt-4 py-6 border-t border-orange-100 bg-white/95 backdrop-blur-lg rounded-b-2xl">
            <nav className="flex flex-col gap-4">
              {['features', 'how-it-works', 'faq'].map((section) => (
                <button 
                  key={section}
                  onClick={() => scrollToSection(section)}
                  className="text-left py-2 font-semibold text-lg text-gray-700 hover:text-orange-500"
                >
                  {t(`nav_${section.replace(/-/g, '_')}`)}
                </button>
              ))}
              <a 
                href="/blog"
                className="text-left py-2 font-semibold text-lg text-gray-700 hover:text-orange-500"
                onClick={() => setMobileMenuOpen(false)}
              >
                Blog
              </a>
              {user ? (
                <>
                  <button onClick={() => { navigate("/dashboard"); setMobileMenuOpen(false); }} className="text-left py-2 font-semibold text-lg gradient-text flex items-center gap-2">
                    <User className="w-5 h-5" />
                    {t("nav_dashboard")}
                  </button>
                  <button onClick={() => { logout(); setMobileMenuOpen(false); }} className="text-left py-2 text-gray-400 flex items-center gap-2">
                    <LogOut className="w-5 h-5" />
                    {t("nav_logout")}
                  </button>
                </>
              ) : (
                <>
                  <button onClick={handleLoginClick} className="text-left py-2 font-semibold text-lg text-gray-700 hover:text-orange-500 flex items-center gap-2">
                    <User className="w-5 h-5" />
                    {language === 'fr' ? 'Connexion / Inscription' : 'Login / Sign up'}
                  </button>
                  <button onClick={() => { onPreorderClick(); setMobileMenuOpen(false); }} className="calli-btn-primary mt-4 w-full">
                    {t("nav_preorder")}
                  </button>
                </>
              )}
            </nav>
          </div>
        )}
      </div>
    </header>
  );
};

export default Header;
